import React, { useState } from 'react';
import { Shuffle, Trash2, Users, Target, TrendingUp, Loader } from 'lucide-react';
import { authService } from '../../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface SegmentStats {
  [key: string]: number;
}

interface GenerationResult {
  success: boolean;
  message: string;
  created_count: number;
  segment_distribution: SegmentStats;
  sample_leads: Array<{
    id: number;
    email: string;
    parent_name: string;
    lead_score: number;
    segments: string[];
  }>;
}

const TestingPanel: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const generateMockLeads = async (count: number = 50) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/leads/generate-mock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({ count })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setShowResults(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate mock leads');
      }
    } catch (err) {
      setError('Network error generating mock leads');
    } finally {
      setLoading(false);
    }
  };

  const clearAllLeads = async () => {
    if (!window.confirm('⚠️ This will delete ALL leads and segment assignments. Are you sure?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/leads/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResult(null);
        setShowResults(false);
        window.alert(`✅ ${data.message}`);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to clear leads');
      }
    } catch (err) {
      setError('Network error clearing leads');
    } finally {
      setLoading(false);
    }
  };

  // Only show in development mode
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Target className="text-purple-600" size={24} />
        <h3 className="text-lg font-semibold text-purple-900">🧪 Development Testing Tools</h3>
        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-medium">
          DEV ONLY
        </span>
      </div>

      <p className="text-gray-600 mb-4">
        Generate realistic mock leads to test your segmentation rules and system functionality.
      </p>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-4">
        <button
          onClick={() => generateMockLeads(25)}
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader size={16} className="animate-spin" />
          ) : (
            <Shuffle size={16} />
          )}
          Generate 25 Leads
        </button>

        <button
          onClick={() => generateMockLeads(50)}
          disabled={loading}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader size={16} className="animate-spin" />
          ) : (
            <Users size={16} />
          )}
          Generate 50 Leads
        </button>

        <button
          onClick={() => generateMockLeads(100)}
          disabled={loading}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader size={16} className="animate-spin" />
          ) : (
            <TrendingUp size={16} />
          )}
          Generate 100 Leads
        </button>

        <button
          onClick={clearAllLeads}
          disabled={loading}
          className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
        >
          {loading ? (
            <Loader size={16} className="animate-spin" />
          ) : (
            <Trash2 size={16} />
          )}
          Clear All Leads
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
          <p className="text-red-700 text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-red-600 underline text-xs mt-1"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
          <div className="flex items-center gap-3">
            <Loader size={20} className="animate-spin text-blue-600" />
            <div>
              <p className="text-blue-800 font-medium">Processing...</p>
              <p className="text-blue-600 text-sm">Generating leads and applying segmentation rules</p>
            </div>
          </div>
        </div>
      )}

      {/* Results Display */}
      {showResults && result && (
        <div className="bg-green-50 border border-green-200 rounded p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-green-800 flex items-center gap-2">
              ✅ Generation Complete
            </h4>
            <button
              onClick={() => setShowResults(false)}
              className="text-green-600 hover:text-green-800"
            >
              ✕
            </button>
          </div>

          <p className="text-green-700 mb-4">{result.message}</p>

          {/* Segment Distribution */}
          {Object.keys(result.segment_distribution).length > 0 && (
            <div className="mb-4">
              <h5 className="font-medium text-green-800 mb-2">Segment Distribution:</h5>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(result.segment_distribution).map(([segment, count]) => (
                  <div
                    key={segment}
                    className="bg-white border border-green-200 rounded px-3 py-2 text-sm"
                  >
                    <div className="font-medium text-gray-900">{segment}</div>
                    <div className="text-green-600">{count} leads</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sample Leads */}
          {result.sample_leads?.length > 0 && (
            <div>
              <h5 className="font-medium text-green-800 mb-2">Sample Generated Leads:</h5>
              <div className="space-y-2">
                {result.sample_leads.map((lead) => (
                  <div
                    key={lead.id}
                    className="bg-white border border-green-200 rounded px-3 py-2 text-sm"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{lead.parent_name}</div>
                        <div className="text-gray-600">{lead.email}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-blue-600">Score: {lead.lead_score}</div>
                        <div className="text-xs text-gray-500">
                          {lead.segments.length} segment(s)
                        </div>
                      </div>
                    </div>
                    {lead.segments.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {lead.segments.map((segment) => (
                          <span
                            key={segment}
                            className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs"
                          >
                            {segment}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <h5 className="font-medium text-blue-800 mb-1">Next Steps:</h5>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• Visit the <strong>Segments</strong> page to view lead distribution</li>
              <li>• Check the <strong>Leads</strong> page to see individual lead details</li>
              <li>• Test segment rules by creating new segments</li>
              <li>• Export segments as CSV when ready for email marketing</li>
            </ul>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded text-sm">
        <h5 className="font-medium text-gray-800 mb-1">How to Use:</h5>
        <ul className="text-gray-600 space-y-1">
          <li>• <strong>Generate Leads:</strong> Creates realistic mock leads with varied characteristics</li>
          <li>• <strong>Auto-Segmentation:</strong> Leads are automatically assigned to matching segments</li>
          <li>• <strong>Clear All:</strong> Removes all leads and segment assignments for fresh testing</li>
          <li>• <strong>Test Scenarios:</strong> Generate different lead types to test your segment rules</li>
        </ul>
      </div>
    </div>
  );
};

export default TestingPanel;